import React, { Fragment, useEffect, useState } from "react"
import { Row, Table, Button, Col, Card, CardBody } from "reactstrap"
import { Link } from "react-router-dom"
import moment from "moment"

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table"

import { rankItem } from "@tanstack/match-sorter-utils"
import searchIcon from "../../assets/images/icons/search.svg"
import leftArrow from "../../assets/images/icons/left-arrow.svg"
import rightArrow from "../../assets/images/icons/right-arrow.svg"
import Spinners from "./Spinner"
import Breadcrumb from "./Breadcrumb"
import { getTableSortingHeaderIcon } from "helpers/common"

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue()

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  )
}

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [debounce, onChange, value])

  return (
    <React.Fragment>
      <input
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </React.Fragment>
  )
}

const TableContainer = ({
  showSerialNo = true,
  noCardDesign = false,
  isLoading = false,
  count = 0,
  filters,
  setFilters,
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered = false,
  isPagination,
  isGlobalFilter,
  HeaderComponent,
  FooterComponent,
  FilterComponents,
  paginationWrapper,
  pagination,
  isCustomPageSize,
}) => {
  const { page, pageSize } = filters
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({
      itemRank,
    })
    return itemRank.passed
  }

  const finalColumns = [
    ...(showSerialNo
      ? [
          {
            id: "srNo",
            header: ({ column }) =>
              getTableSortingHeaderIcon(column, "Sr. No."),
            accessorFn: (_row, index) => (page - 1) * pageSize + index + 1,
            enableSorting: true,
            enableColumnFilter: false,
            cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
          },
        ]
      : []),
    ...columns,
  ]
  const table = useReactTable({
    columns: finalColumns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    pageCount: Math.ceil(count / pageSize),
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
      columnFilters,
      globalFilter,
    },
    manualPagination: true,
    enableSortingRemoval: false,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function"
          ? updater({ pageIndex: page - 1, pageSize })
          : updater

      setFilters((prev) => ({
        ...prev,
        page: next.pageIndex + 1,
        pageSize: next.pageSize,
      }))
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState,
  } = table

  // useEffect(() => {
  //   Number(customPageSize) && setPageSize(Number(customPageSize));
  // }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <Card style={noCardDesign ? { padding: "0px", boxShadow: "none" } : {}}>
        <CardBody style={noCardDesign ? { padding: "0px" } : {}}>
          {HeaderComponent && <HeaderComponent />}
          {(isGlobalFilter || isCustomPageSize) && (
            <Row className="mb-4 align-items-center ">
              <Col sm={12}>
                <div className="d-flex align-items-center gap-2 justify-content-start justify-content-md-end mt-3 mt-md-0">
                  {isGlobalFilter && (
                    <div className="table-search">
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(String(value))}
                        className="form-control search-box"
                        placeholder={"Search"}
                      />
                      <div className="search">
                        <img src={searchIcon} alt="menuBurger" />
                      </div>
                    </div>
                  )}
                  {FilterComponents && <FilterComponents />}
                </div>
              </Col>
              {/* <Col xl={12}>
          <div className="d-flex align-items-center gap-3 mt-3">
            <div className="status-filter-wrapper">
              <select name="entity_type" className="form-select pageSize">
                <option value={""}>Status</option>
                <option value={"Active"}>Active</option>
                <option value={"Archived"}>Archived</option>
                <option value={"Inactive"}>Inactive</option>
              </select>
              <span className="approved"></span>
              <span className="archived"></span>
              <span className="inactive"></span>
            </div>
            <div className="tenure-filter-wrapper">
              <select name="entity_type" className="form-select pageSize">
                <option value={""}>Tenure</option>
                <option value={"3 years"}>3 years</option>
                <option value={"2 years"}>2 years</option>
                <option value={"5 years"}>5 years</option>
                <option value={"8 years"}>8 years</option>
              </select>
              <img src={clock} alt="" />
            </div>
          </div>
        </Col> */}
            </Row>
          )}
          <div className="table-bordered-wrapper">
            <div className={divClassName ? divClassName : "table-responsive"}>
              <Table hover className={tableClass} bordered={isBordered}>
                <thead className={theadClass}>
                  {getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            className={`${
                              header.column.columnDef.enableSorting
                                ? "sorting sorting_desc"
                                : ""
                            }`}
                          >
                            {header.isPlaceholder ? null : (
                              <React.Fragment>
                                <div
                                  {...{
                                    className: header.column.getCanSort()
                                      ? "cursor-pointer select-none"
                                      : "",
                                    onClick:
                                      header.column.getToggleSortingHandler(),
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {{
                                    asc: "",
                                    desc: "",
                                  }[header.column.getIsSorted()] ?? null}
                                </div>
                                {header.column.getCanFilter() ? (
                                  <div>
                                    <Filter
                                      column={header.column}
                                      table={table}
                                    />
                                  </div>
                                ) : null}
                              </React.Fragment>
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className="p-2 text-center">
                          <Spinners color="dark" />
                        </div>
                      </td>
                    </tr>
                  )}
                  {!isLoading && table.getRowModel().rows.length > 0 && (
                    <>
                      {getRowModel().rows.map((row) => {
                        const deleted_at = row?.original?.deleted_at
                        let isOlderThan7Days = false
                        if (deleted_at) {
                          isOlderThan7Days =
                            moment().diff(moment(deleted_at), "days") > 7
                        }

                        return (
                          <tr
                            key={row.id}
                            className={isOlderThan7Days ? "is-deleted" : ""}
                          >
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <td key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </>
                  )}
                  {!isLoading && table.getRowModel().rows.length <= 0 && (
                    <tr>
                      <td colSpan={columns.length + 1}>
                        <div className="p-2 text-center">No data available</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {isPagination && (
              <Row>
                {/* <Col sm={12} md={5}>
              <div className="dataTables_info">
                Showing {getState().pagination.pageSize} of {data.length} Results
              </div>
            </Col> */}
                <Col sm={12} md={12}>
                  <div className={paginationWrapper}>
                    <div className={pagination}>
                      <div className="table-pagination">
                        <div className="table-data-filter-wrapper">
                          {isCustomPageSize && (
                            <>
                              <div className="table-data-filter">
                                <select
                                  className="form-select pageSize"
                                  value={table.getState().pagination.pageSize}
                                  onChange={(e) => {
                                    table.setPageSize(Number(e.target.value))
                                  }}
                                >
                                  {[20, 30, 40, 50, 100].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                      {pageSize}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="table-data-entries">
                                {" "}
                                entries per page
                              </div>
                            </>
                          )}
                        </div>
                        <div className="page-number-wrapper">
                          {getPageOptions().map((item, key) => (
                            <div
                              key={key}
                              className={`page-number paginate_button page-item ${
                                getState().pagination.pageIndex === item
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <Link
                                to="#"
                                className="page-link"
                                onClick={() => setPageIndex(item)}
                              >
                                {item + 1}
                              </Link>
                            </div>
                          ))}
                        </div>

                        <div className="pagination-arrows">
                          <div
                            className={`paginate_button page-item previous ${
                              !getCanPreviousPage() ? "disabled" : ""
                            }`}
                          >
                            <Link
                              to="#"
                              className="page-link"
                              onClick={previousPage}
                            >
                              <img src={leftArrow} alt="" /> Previous
                            </Link>
                          </div>
                          <div
                            className={`paginate_button page-item next ${
                              !getCanNextPage() ? "disabled" : ""
                            }`}
                          >
                            <Link
                              to="#"
                              className="page-link"
                              onClick={nextPage}
                            >
                              Next <img src={rightArrow} alt="" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </div>

          {FooterComponent && <FooterComponent />}
        </CardBody>
      </Card>
    </Fragment>
  )
}

export default TableContainer
